import { Button } from '@ads/components-react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import illustrationError from '@/assets/svg/notifications-error.svg';
import {
  NotificationCreateTemplate,
  NotificationWizard,
  useNotificationEdit,
  useNotificationWizard,
} from '@/features/notification';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

export const Route = createFileRoute('/_authenticated/notification/$id/')({
  validateSearch: z.object({
    step: z.number().int().min(0).max(2).optional().default(0),
  }),
  component: NotificationEditPage,
});

function NotificationEditPage() {
  const { id } = Route.useParams();
  const notificationId = Number(id);
  const { reset } = useNotificationWizard();
  const { t } = useTranslation('notification');
  const { isLoading, isError, refetch } = useNotificationEdit(notificationId);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  if (isLoading) {
    return (
      <NotificationCreateTemplate>
        <div className="flex flex-col gap-sm p-xxs">
          <div className="flex flex-col gap-nano">
            <Skeleton className="h-xxs w-48" />
            <Skeleton className="h-mini w-72" />
          </div>

          <div className="flex flex-col border-hairline border-ctx-layout-border rounded-md">
            <div className="flex items-center justify-between gap-sm px-xxs py-micro border-b-hairline border-ctx-layout-border">
              <Skeleton className="h-mini w-24 shrink-0" />
              <Skeleton className="h-xs w-full max-w-[420px]" />
            </div>
            <div className="flex items-center justify-between px-xxs py-micro border-b-hairline border-ctx-layout-border">
              <Skeleton className="h-micro w-40" />
              <Skeleton className="h-xs w-24" />
            </div>
            <div className="flex flex-col">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-mini px-xxs py-nano border-b-hairline border-ctx-layout-border last:border-b-none"
                >
                  <Skeleton className="size-[16px] shrink-0" />
                  <Skeleton className="h-micro flex-1" />
                  <Skeleton className="h-xxxs w-[4rem] shrink-0" />
                </div>
              ))}
            </div>
            <div className="flex justify-center py-nano">
              <Skeleton className="h-xs w-48" />
            </div>
          </div>

          <div className="flex flex-col border-hairline border-ctx-layout-border rounded-md">
            <div className="flex items-center justify-between px-xxs py-micro border-b-hairline border-ctx-layout-border">
              <Skeleton className="h-mini w-32" />
              <Skeleton className="h-xs w-24" />
            </div>
            <div className="grid grid-cols-2 gap-mini px-xxs py-micro">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-nano">
                  <Skeleton className="size-mini shrink-0" />
                  <Skeleton className="h-micro flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </NotificationCreateTemplate>
    );
  }

  if (isError) {
    return (
      <NotificationCreateTemplate>
        <div className="flex justify-center items-center p-xxl">
          <EmptyState
            illustration={illustrationError}
            title={t('wizard.emptyState.title')}
            description={t('wizard.emptyState.description')}
            action={
              <Button leadingIcon={ArrowPathIcon} size="md" onClick={() => void refetch()}>
                {t('error.retry')}
              </Button>
            }
          />
        </div>
      </NotificationCreateTemplate>
    );
  }

  return (
    <NotificationCreateTemplate>
      <NotificationWizard />
    </NotificationCreateTemplate>
  );
}
